import React, { useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode';
import {
  AlertCircle,
  Check,
  Copy,
  QrCode,
  Shield,
  Smartphone,
  X,
} from 'lucide-react';
import {
  AdminAuthError,
  PasswordPolicy,
  useAdminAuth,
} from '../contexts/AdminAuthContext';

/**
 * Admin sign-in (FO-71).
 *
 * Every step is driven by the server's `status` — the portal never decides what
 * comes next, it only renders it:
 *
 *   1. email + password               -> POST /admin-auth/login
 *   2. choose a password (first login) -> POST /admin-auth/password/set
 *   3. set up an authenticator         -> POST /admin-auth/mfa/enroll
 *   4. TOTP code (every later sign-in) -> POST /admin-auth/mfa/verify
 *
 * Steps 2 and 3 are the invite flow: an admin created with `admin-create-user`
 * arrives with an emailed temporary password and leaves with a password of
 * their own and an enrolled authenticator. Neither is skippable, and no session
 * exists until both are done.
 *
 * Abandoning half way is safe: signing in again resumes at whichever step is
 * still outstanding, because the server keeps no first-login state to go stale.
 *
 * Break-glass (the shared token) is only offered when the server says it is
 * still accepted — i.e. on a deployment with no Cognito-backed admin path.
 */

const inputClass =
  'w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl focus:ring-2 ' +
  'focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder-gray-400';

const buttonClass =
  'w-full bg-blue-600 text-white py-3 px-4 rounded-xl font-semibold hover:bg-blue-700 ' +
  'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg';

const codeInputClass = `${inputClass} tracking-[0.4em] text-center text-lg`;

/** Groups the 32-char base32 secret into 4s so it can be typed without losing place. */
function formatSecret(secret: string | undefined): string {
  if (!secret) return '';
  return (secret.match(/.{1,4}/g) || [secret]).join(' ');
}

/**
 * Mirror of the server's `_password_policy_violations`, used only to light up
 * the checklist as the admin types. Cognito is still the enforcer — this never
 * decides whether a password is submitted, only what the list looks like.
 */
function meetsRequirement(id: string, value: string, minimum: number): boolean {
  switch (id) {
    case 'length':
      return value.length >= minimum;
    case 'uppercase':
      return /[A-Z]/.test(value);
    case 'lowercase':
      return /[a-z]/.test(value);
    case 'numbers':
      return /[0-9]/.test(value);
    case 'symbols':
      return /[^A-Za-z0-9\s]/.test(value);
    default:
      return true;
  }
}

const PolicyChecklist: React.FC<{ policy: PasswordPolicy; value: string }> = ({
  policy,
  value,
}) => (
  <ul className="space-y-1.5">
    {(policy.requirements ?? []).map((req) => {
      const ok = meetsRequirement(req.id, value, policy.minimum_length);
      return (
        <li
          key={req.id}
          className={`flex items-center space-x-2 text-sm ${
            ok ? 'text-green-700' : 'text-gray-500'
          }`}
        >
          {ok ? (
            <Check className="w-4 h-4 flex-shrink-0" />
          ) : (
            <X className="w-4 h-4 flex-shrink-0 text-gray-300" />
          )}
          <span>{req.label}</span>
        </li>
      );
    })}
  </ul>
);

/** "Step 2 of 3" rail, shown only while an admin is being onboarded. */
const StepRail: React.FC<{ current: 1 | 2 }> = ({ current }) => (
  <div className="flex items-center space-x-2 mb-6">
    {[1, 2].map((n) => (
      <div
        key={n}
        className={`h-1.5 flex-1 rounded-full ${
          n <= current ? 'bg-blue-600' : 'bg-gray-200'
        }`}
      />
    ))}
    <span className="text-xs font-medium text-gray-500 pl-2">Step {current} of 2</span>
  </div>
);

export const AdminLogin: React.FC = () => {
  const {
    stage,
    config,
    pendingEmail,
    enrollment,
    passwordPolicy,
    sessionNotice,
    login,
    setNewPassword,
    submitMfaCode,
    submitEnrollmentCode,
    cancelChallenge,
  } = useAdminAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPasswordValue] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  // A notice explains why the operator is back here (session expired, MFA
  // not fully enrolled, admin rights removed). The form's own error always
  // wins: it describes what just failed, which is more immediate.
  const banner = error || sessionNotice || '';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [copied, setCopied] = useState(false);

  const run = async (action: () => Promise<void>) => {
    setError('');
    setIsSubmitting(true);
    try {
      await action();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Sign-in failed. Check the email and password, or use the temporary password from your invite.',
      );
      // A dead ticket cannot be retried from this form; send the admin back to
      // the start rather than letting them type into a step that can't succeed.
      if (
        err instanceof AdminAuthError &&
        (err.code === 'challenge_expired' || err.code === 'enrollment_expired')
      ) {
        cancelChallenge();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const onPassword = (e: React.FormEvent) => {
    e.preventDefault();
    void run(() => login(email, password));
  };

  const onNewPassword = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Those two passwords don’t match.');
      return;
    }
    void run(async () => {
      await setNewPassword(newPassword);
      // The password has been accepted by Cognito and is now permanent; there is
      // nothing left to resubmit, so drop it rather than leave it in a field.
      setNewPasswordValue('');
      setConfirmPassword('');
      setPassword('');
    });
  };

  const onCode = (e: React.FormEvent) => {
    e.preventDefault();
    void run(async () => {
      if (stage === 'mfa_enrollment_required') {
        await submitEnrollmentCode(code);
      } else {
        await submitMfaCode(code);
      }
      setCode('');
    });
  };

  const restart = () => {
    setCode('');
    setPassword('');
    setNewPasswordValue('');
    setConfirmPassword('');
    setError('');
    cancelChallenge();
  };

  // Render the otpauth:// URI as a scannable QR. Drawn locally from the URI —
  // the secret never goes to a QR service.
  useEffect(() => {
    const uri = enrollment?.otpauth_uri;
    if (!uri) {
      setQrDataUrl('');
      return;
    }
    let cancelled = false;
    QRCode.toDataURL(uri, {
      errorCorrectionLevel: 'M',
      margin: 1,
      width: 200,
      color: { dark: '#111827', light: '#ffffff' },
    })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        // Not fatal — the copyable key below is a complete alternative.
        if (!cancelled) setQrDataUrl('');
      });
    return () => {
      cancelled = true;
    };
  }, [enrollment?.otpauth_uri]);

  const copySecret = async () => {
    if (!enrollment?.secret_code) return;
    try {
      await navigator.clipboard.writeText(enrollment.secret_code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy the key — select it and copy manually.');
    }
  };

  // The server is the authority on which methods exist; until /config answers
  // we optimistically show the password form (the normal case).
  const cognitoAvailable = config ? config.cognito_login : true;

  // Only the invite flow reaches enrolment via the password step; an admin who
  // is merely re-enrolling should not be told they are "step 2 of 2".
  const firstLogin = stage === 'new_password_required' || Boolean(enrollment?.challenge_ticket);

  const policy: PasswordPolicy = useMemo(
    () =>
      passwordPolicy ?? {
        minimum_length: 14,
        requirements: [{ id: 'length', label: 'At least 14 characters' }],
        description: 'Passwords must be at least 14 characters.',
      },
    [passwordPolicy],
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center p-8">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-4 shadow-lg">
            <Shield className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">SuprAgent Admin Portal</h1>
          <p className="text-gray-600">Authorized access only</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
          {banner && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-red-700">{banner}</span>
            </div>
          )}

          {stage === 'loading' && (
            <p className="text-center text-sm text-gray-500 py-6">Checking your session…</p>
          )}

          {/* ---------- First login, step 1: choose a password --------------- */}
          {stage === 'new_password_required' && (
            <div className="space-y-5">
              <StepRail current={1} />

              <div>
                <h2 className="text-xl font-bold text-gray-900">Set a new password</h2>
                <p className="mt-1 text-sm text-gray-600">
                  {pendingEmail} was invited with a temporary password. Choose your own to
                  finish setting up the account.
                </p>
              </div>

              <form onSubmit={onNewPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    New password
                  </label>
                  <input
                    type="password"
                    required
                    autoFocus
                    className={inputClass}
                    placeholder="New password"
                    value={newPassword}
                    onChange={(e) => setNewPasswordValue(e.target.value)}
                    autoComplete="new-password"
                  />
                </div>

                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <PolicyChecklist policy={policy} value={newPassword} />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Confirm password
                  </label>
                  <input
                    type="password"
                    required
                    className={inputClass}
                    placeholder="Re-enter the password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                  {confirmPassword && confirmPassword !== newPassword && (
                    <p className="mt-2 text-sm text-amber-700">
                      These two passwords don&apos;t match yet.
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || !newPassword || newPassword !== confirmPassword}
                  className={buttonClass}
                >
                  {isSubmitting ? 'Saving…' : 'Set password and continue'}
                </button>
              </form>

              <button
                type="button"
                onClick={restart}
                className="w-full text-sm text-gray-500 hover:text-gray-700"
              >
                Start over
              </button>
            </div>
          )}

          {/* ---------- Authenticator setup ---------------------------------- */}
          {stage === 'mfa_enrollment_required' && enrollment && (
            <div className="space-y-5">
              {firstLogin && <StepRail current={2} />}

              <div>
                <h2 className="text-xl font-bold text-gray-900">Set up two-factor</h2>
                <p className="mt-1 text-sm text-gray-600">
                  Admin accounts require an authenticator app. This step can&apos;t be skipped
                  — you&apos;ll be signed in as soon as it&apos;s done.
                </p>
              </div>

              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">
                  1. Scan this with your authenticator app
                </p>
                <div className="flex justify-center rounded-xl border border-gray-200 bg-gray-50 p-4">
                  {qrDataUrl ? (
                    <img
                      src={qrDataUrl}
                      alt="QR code for adding this account to your authenticator app"
                      width={200}
                      height={200}
                      className="rounded-lg bg-white"
                    />
                  ) : (
                    <div className="flex h-[200px] w-[200px] flex-col items-center justify-center gap-2 text-xs text-gray-500">
                      <QrCode className="w-6 h-6" />
                      <span className="text-center">
                        QR code unavailable — enter the key below manually.
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">
                  2. Or add the key by hand
                </p>
                <div className="flex items-stretch gap-2">
                  <code className="flex-1 select-all px-3 py-2 bg-gray-100 border border-gray-300 rounded-xl text-sm font-mono tracking-[0.1em] break-all text-gray-900">
                    {formatSecret(enrollment.secret_code)}
                  </code>
                  <button
                    type="button"
                    onClick={copySecret}
                    title="Copy setup key"
                    className="shrink-0 px-3 rounded-xl border border-gray-300 text-xs font-medium text-gray-600 hover:bg-gray-100 transition-colors"
                  >
                    {copied ? (
                      <span className="flex items-center gap-1.5">
                        <Check className="w-3.5 h-3.5" /> Copied
                      </span>
                    ) : (
                      <span className="flex items-center gap-1.5">
                        <Copy className="w-3.5 h-3.5" /> Copy
                      </span>
                    )}
                  </button>
                </div>
              </div>

              <form onSubmit={onCode} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    3. Enter the 6-digit code
                  </label>
                  <input
                    type="text"
                    required
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    className={codeInputClass}
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting || code.length < 6}
                  className={buttonClass}
                >
                  {isSubmitting ? 'Verifying…' : 'Verify and sign in'}
                </button>
              </form>

              <button
                type="button"
                onClick={restart}
                className="w-full text-sm text-gray-500 hover:text-gray-700"
              >
                Start over
              </button>
            </div>
          )}

          {/* ---------- TOTP challenge (every later sign-in) ------------------ */}
          {stage === 'mfa_required' && (
            <div className="space-y-5">
              <div className="flex items-start space-x-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <Smartphone className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-semibold">Two-factor authentication</p>
                  <p className="mt-1">
                    Enter the current code for {pendingEmail} from your authenticator app.
                  </p>
                </div>
              </div>

              <form onSubmit={onCode} className="space-y-4">
                <input
                  type="text"
                  required
                  autoFocus
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  className={codeInputClass}
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
                />
                <button
                  type="submit"
                  disabled={isSubmitting || code.length < 6}
                  className={buttonClass}
                >
                  {isSubmitting ? 'Verifying…' : 'Verify'}
                </button>
              </form>

              <button
                type="button"
                onClick={restart}
                className="w-full text-sm text-gray-500 hover:text-gray-700"
              >
                Use a different account
              </button>
            </div>
          )}

          {/* ---------- Sign in ---------------------------------------------- */}
          {stage === 'unauthenticated' && (
            <>
              {cognitoAvailable ? (
                <form onSubmit={onPassword} className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Admin email
                    </label>
                    <input
                      type="email"
                      required
                      className={inputClass}
                      placeholder="you@supragent.ai"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="username"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Password
                    </label>
                    <input
                      type="password"
                      required
                      className={inputClass}
                      placeholder="Your admin password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                    />
                    <p className="mt-2 text-xs text-gray-500">
                      First time here? Use the temporary password from your invitation email —
                      you&apos;ll choose your own next.
                    </p>
                  </div>

                  <button type="submit" disabled={isSubmitting} className={buttonClass}>
                    {isSubmitting ? 'Signing in…' : 'Sign in'}
                  </button>
                </form>
              ) : (
                <p className="text-sm text-gray-600">
                  Admin sign-in isn&apos;t available on this deployment. Contact the platform team.
                </p>
              )}

            </>
          )}

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              This portal is for authorized administrators only.
              <br />
              Unauthorized access is prohibited.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
